import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Menu, X } from "lucide-react";
import axios from "axios";
import { API } from "../App";
import LanguageCurrencySelector from "../components/LanguageCurrencySelector";

const LOGO_URL = "/images/singleseen_logo_transparent.png";
const HERO_BG_URL = "https://static.prod-images.emergentagent.com/jobs/57ecaa6c-85f3-4aaa-9a5e-4c0e784e64b9/images/bdf323f888387b363b513ed1fa10ca92711e5ae3b8c59c746be6ec5c545963f1.png";

const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quote, setQuote] = useState(null);
  const [loveStories, setLoveStories] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [logoClickCount, setLogoClickCount] = useState(0);
  const logoClickTimeout = useRef(null);

  // Secret admin access - click logo 5 times quickly
  const handleLogoClick = () => {
    setLogoClickCount(prev => prev + 1);
    
    if (logoClickTimeout.current) {
      clearTimeout(logoClickTimeout.current);
    }
    
    logoClickTimeout.current = setTimeout(() => {
      setLogoClickCount(0);
    }, 2000);
    
    if (logoClickCount + 1 >= 5) {
      navigate('/admin');
      setLogoClickCount(0);
    }
  };

  useEffect(() => {
    fetchQuote();
    fetchLoveStories();
    fetchTestimonials();
  }, []);

  const fetchQuote = async () => {
    try {
      const response = await axios.get(`${API}/quotes/random`);
      setQuote(response.data);
    } catch (error) {
      console.error("Error fetching quote:", error);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials?featured_only=true`);
      if (response.data && response.data.length > 0) {
        setTestimonials(response.data);
      }
    } catch (error) {
      console.error("Error fetching testimonials:", error);
    }
  };

  const fetchLoveStories = async () => {
    try {
      const response = await axios.get(`${API}/love-stories`);
      setLoveStories(response.data);
    } catch (error) {
      // Use sample stories if API not available yet
      setLoveStories([
        {
          id: "1",
          couple_names: "Ahmed & Fatima",
          location: "London, UK",
          story: t('landing.loveStory1'),
          photo_url: "https://images.unsplash.com/photo-1617881360412-6f9b11e66267?w=400",
          married_date: "2024"
        },
        {
          id: "2", 
          couple_names: "Omar & Aisha",
          location: "Toronto, Canada",
          story: t('landing.loveStory2'),
          photo_url: "https://images.unsplash.com/photo-1519741497674-611481863552?w=400",
          married_date: "2023"
        },
        {
          id: "3",
          couple_names: "Yusuf & Mariam",
          location: "Dubai, UAE",
          story: t('landing.loveStory3'),
          photo_url: "https://images.unsplash.com/photo-1529634597503-139d3726fed5?w=400",
          married_date: "2024"
        }
      ]);
    }
  };

  const features = [
    {
      number: "01",
      title: t('landing.feature1Title'),
      description: t('landing.feature1Desc')
    },
    {
      number: "02",
      title: t('landing.feature2Title'),
      description: t('landing.feature2Desc')
    },
    {
      number: "03",
      title: t('landing.feature3Title'),
      description: t('landing.feature3Desc')
    },
    {
      number: "04",
      title: t('landing.feature4Title'),
      description: t('landing.feature4Desc')
    }
  ];

  const stats = [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-amber-50/30">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-rose-100/50" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-36">
            {/* Logo - click 5 times for admin access */}
            <div onClick={handleLogoClick} className="flex items-center gap-2 cursor-pointer" data-testid="logo-link">
              <img src={LOGO_URL} alt="SingleSeen" className="h-36 w-auto" />
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <Link to="/about" className="text-rose-700 hover:text-rose-900 transition-colors" data-testid="nav-about">
                {t('nav.about')}
              </Link>
              <Link to="/love-stories" className="text-rose-700 hover:text-rose-900 transition-colors font-medium" data-testid="nav-love-stories">
                {t('nav.loveStories')}
              </Link>
              <Link to="/pricing" className="text-rose-700 hover:text-rose-900 transition-colors" data-testid="nav-pricing">
                {t('nav.pricing')}
              </Link>
              <LanguageCurrencySelector />
              {user ? (
                <Button 
                  onClick={() => navigate("/dashboard")} 
                  className="bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 rounded-full px-6 shadow-lg shadow-rose-500/30"
                  data-testid="nav-dashboard-btn"
                >
                  {t('nav.dashboard')}
                </Button>
              ) : (
                <div className="flex items-center gap-4">
                  <Link to="/login" className="text-rose-700 hover:text-rose-900 transition-colors" data-testid="nav-login">
                    {t('nav.login')}
                  </Link>
                  <Button 
                    onClick={() => navigate("/register")} 
                    className="bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 rounded-full px-6 shadow-lg shadow-rose-500/30"
                    data-testid="nav-register-btn"
                  >
                    {t('nav.getStarted')}
                  </Button>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 text-rose-700"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-rose-100 py-4 px-4" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              <Link to="/about" className="text-rose-700 py-2">{t('nav.about')}</Link>
              <Link to="/love-stories" className="text-rose-700 py-2 font-medium">{t('nav.loveStories')}</Link>
              <Link to="/pricing" className="text-rose-700 py-2">{t('nav.pricing')}</Link>
              <div className="py-2">
                <LanguageCurrencySelector />
              </div>
              {user ? (
                <Button onClick={() => navigate("/dashboard")} className="bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-full">
                  {t('nav.dashboard')}
                </Button>
              ) : (
                <>
                  <Link to="/login" className="text-rose-700 py-2">{t('nav.login')}</Link>
                  <Button onClick={() => navigate("/register")} className="bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-full">
                    {t('nav.getStarted')}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-36 pb-0 md:pt-44 md:pb-0 relative overflow-hidden" data-testid="hero-section">
        {/* Full-width background image at 50% opacity */}
        <div className="absolute inset-0">
          <img 
            src={HERO_BG_URL}
            alt=""
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-rose-50/70 via-white/40 to-white" />
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center py-20 md:py-32">
          <p className="text-rose-600 font-medium text-sm tracking-wide uppercase mb-6" data-testid="hero-badge">
            {t('hero.badge')}
          </p>
          
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-rose-900 leading-tight tracking-tight mb-6">
            {t('hero.title')} <span className="gradient-text">{t('hero.titleHighlight')}</span> {t('hero.titleEnd')}
          </h1>
          
          <p className="text-lg text-rose-700/80 leading-relaxed max-w-2xl mx-auto mb-10">
            {t('hero.subtitle')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate("/register")}
              className="bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 shadow-lg shadow-rose-500/30 hover:shadow-rose-500/40 transition-all duration-300 rounded-full px-8 py-6 text-lg font-medium"
              data-testid="hero-cta-btn"
            >
              {t('hero.cta')}
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate("/love-stories")}
              className="border-2 border-rose-300 text-rose-700 hover:bg-rose-50 rounded-full px-8 py-6 text-lg bg-white/60 backdrop-blur-sm"
              data-testid="hero-stories-btn"
            >
              {t('hero.stories')}
            </Button>
          </div>
        </div>
      </section>

      {/* Islamic Quote Banner */}
      {quote && (
        <section className="py-12 bg-gradient-to-r from-rose-500 via-pink-500 to-rose-600" data-testid="quote-banner">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="font-accent text-2xl text-rose-100 mb-2">بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيمِ</p>
            <p className="text-white text-lg leading-relaxed italic">"{quote.text}"</p>
            <p className="text-rose-200 mt-4 text-sm font-medium">— {quote.source}</p>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl text-rose-900 mb-4">Why Choose SingleSeen?</h2>
            <p className="text-rose-600">Built differently to protect your dignity and help you find a serious life partner.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="group bg-white border border-rose-100 hover:border-rose-300 transition-all duration-300 rounded-2xl p-8"
                data-testid={`feature-card-${index}`}
              >
                <span className="text-4xl font-light text-rose-300 group-hover:text-rose-500 transition-colors">{feature.number}</span>
                <h3 className="font-serif text-xl text-rose-900 mt-4 mb-3">{feature.title}</h3>
                <p className="text-rose-600/80 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-20 bg-gradient-to-b from-white to-rose-50" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left - Story */}
            <div className="space-y-6">
              <span className="text-rose-500 font-medium tracking-wide uppercase text-sm">{t('about.whySeenTitle')}</span>
              
              <h2 className="font-serif text-3xl sm:text-4xl text-rose-900 leading-tight">
                {t('landing.whyWeBuiltTitle')}
              </h2>
              
              <div className="space-y-4 text-rose-700 leading-relaxed">
                <p>{t('landing.whyWeBuiltP1')}</p>
                <p>{t('landing.whyWeBuiltP2')}</p>
                <p>{t('landing.whyWeBuiltP3')}</p>
              </div>
              
              <div className="pt-4">
                <Button 
                  onClick={() => navigate("/register")}
                  className="bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 rounded-full px-8 py-6 text-lg shadow-lg shadow-rose-500/30"
                >
                  {t('landing.joinCommunity')}
                </Button>
              </div>
            </div>

            {/* Right - Problems We Solve */}
            <div className="space-y-6">
              <h3 className="font-serif text-2xl text-rose-900 mb-8">{t('landing.problemsTitle')}</h3>
              
              {/* Problem Cards - Clean text-based design */}
              <div className="space-y-4">
                <div className="bg-white rounded-2xl p-6 border border-rose-100 hover:border-rose-200 transition-colors">
                  <div className="flex items-start gap-4">
                    <span className="text-2xl font-light text-rose-300">01</span>
                    <div>
                      <h4 className="font-serif text-rose-900 mb-2">{t('landing.problem1Title')}</h4>
                      <p className="text-rose-600/80 text-sm leading-relaxed">{t('landing.problem1Desc')}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 border border-rose-100 hover:border-rose-200 transition-colors">
                  <div className="flex items-start gap-4">
                    <span className="text-2xl font-light text-rose-300">02</span>
                    <div>
                      <h4 className="font-serif text-rose-900 mb-2">{t('landing.problem2Title')}</h4>
                      <p className="text-rose-600/80 text-sm leading-relaxed">{t('landing.problem2Desc')}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 border border-rose-100 hover:border-rose-200 transition-colors">
                  <div className="flex items-start gap-4">
                    <span className="text-2xl font-light text-rose-300">03</span>
                    <div>
                      <h4 className="font-serif text-rose-900 mb-2">{t('landing.problem3Title')}</h4>
                      <p className="text-rose-600/80 text-sm leading-relaxed">{t('landing.problem3Desc')}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 border border-rose-100 hover:border-rose-200 transition-colors">
                  <div className="flex items-start gap-4">
                    <span className="text-2xl font-light text-rose-300">04</span>
                    <div>
                      <h4 className="font-serif text-rose-900 mb-2">{t('landing.problem4Title')}</h4>
                      <p className="text-rose-600/80 text-sm leading-relaxed">{t('landing.problem4Desc')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Meaning of "Seen" - From About Page */}
      <section className="py-20 bg-white" data-testid="meaning-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="flex justify-center md:justify-start">
              <div className="w-36 h-36 bg-gradient-to-br from-rose-100 to-pink-200 rounded-3xl flex items-center justify-center shadow-lg shadow-rose-200/50">
                <span className="text-8xl font-serif text-rose-700" style={{ fontFamily: "'Amiri', serif" }}>س</span>
              </div>
            </div>
            <div className="space-y-4">
              <h2 className="font-serif text-3xl sm:text-4xl text-rose-900">Why "Seen"?</h2>
              <p className="text-rose-700 leading-relaxed">
                The name <strong className="text-rose-900">SingleSeen</strong> carries a deeper meaning rooted in our faith. <em>Seen</em> (س) is the Arabic letter that begins the word <strong className="text-rose-900">Sunnah</strong> — the way of our beloved Prophet Muhammad &#xFE0E;&#xFE0E;.
              </p>
              <p className="text-rose-700 leading-relaxed">
                It is <strong className="text-rose-900">Sunnah to marry</strong>. The Prophet said: <em>"Marriage is part of my Sunnah, and whoever does not follow my Sunnah has nothing to do with me."</em> (Ibn Majah)
              </p>
              <p className="text-rose-700 leading-relaxed">
                Marriage completes half your deen. Your spouse is not just a partner — they are a means to draw closer to Allah.
              </p>
              <Link 
                to="/about" 
                className="inline-flex items-center gap-2 text-rose-500 hover:text-rose-700 font-medium transition-colors mt-2"
                data-testid="read-more-about-link"
              >
              {t('landing.readOurStory')} →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Guidance from the Sunnah - From About Page */}
      <section className="py-20 bg-gradient-to-b from-rose-50/50 to-white" data-testid="guidance-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-rose-500 font-medium tracking-wide uppercase text-sm mb-4 block">{t('about.guidanceFromSunnah')}</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-rose-900 mb-4">{t('about.guidanceTitle')}</h2>
            <p className="text-rose-600">{t('about.guidanceSubtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                num: "1",
                title: t('about.guidance1Title'),
                desc: t('about.guidance1Desc'),
                source: "Bukhari & Muslim"
              },
              {
                num: "2",
                title: t('about.guidance2Title'),
                desc: t('about.guidance2Desc'),
                source: "Tirmidhi"
              },
              {
                num: "3",
                title: t('about.guidance3Title'),
                desc: t('about.guidance3Desc'),
                source: ""
              },
              {
                num: "4",
                title: t('about.guidance4Title'),
                desc: t('about.guidance4Desc'),
                source: ""
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 border border-rose-100 hover:border-rose-200 transition-colors" data-testid={`guidance-card-${index}`}>
                <span className="text-3xl font-light text-rose-300">{item.num}</span>
                <h3 className="font-serif text-rose-900 mt-3 mb-2">{item.title}</h3>
                <p className="text-rose-600/80 text-sm leading-relaxed">{item.desc}</p>
                {item.source && <p className="text-rose-400 text-xs mt-3 italic">— {item.source}</p>}
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link 
              to="/about#guidance" 
              className="inline-flex items-center gap-2 text-rose-500 hover:text-rose-700 font-medium transition-colors"
              data-testid="full-guidance-link"
            >
              {t('landing.readFullGuidance')} →
            </Link>
          </div>
        </div>
      </section>

      {/* Safety Testimonials Section */}
      <section className="py-20 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-rose-500 font-medium tracking-wide uppercase text-sm mb-4 block">{t('landing.userSafetyStories')}</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-rose-900 mb-4">{t('landing.safetyTitle')}</h2>
            <p className="text-rose-600">{t('landing.safetySubtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Amina K.",
                location: "Birmingham, UK",
                avatar: "A",
                quote: t('landing.testimonial1Quote'),
                highlight: t('landing.testimonial1Highlight')
              },
              {
                name: "Tariq M.",
                location: "Manchester, UK",
                avatar: "T",
                quote: t('landing.testimonial2Quote'),
                highlight: t('landing.testimonial2Highlight')
              },
              {
                name: "Zara H.",
                location: "London, UK",
                avatar: "Z",
                quote: t('landing.testimonial3Quote'),
                highlight: t('landing.testimonial3Highlight')
              }
            ].map((testimonial, index) => (
              <div 
                key={index}
                className="bg-gradient-to-b from-rose-50 to-white rounded-2xl p-8 border border-rose-100"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-rose-400 to-pink-400 flex items-center justify-center text-white font-serif text-xl">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <h4 className="font-semibold text-rose-900">{testimonial.name}</h4>
                    <p className="text-rose-500 text-sm">{testimonial.location}</p>
                  </div>
                </div>
                <blockquote className="text-rose-700 leading-relaxed mb-4">
                  "{testimonial.quote}"
                </blockquote>
                <div className="bg-emerald-50 text-emerald-700 px-3 py-2 rounded-lg text-sm font-medium inline-block">
                  {testimonial.highlight}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Love Stories Section */}
      <section className="py-20 bg-gradient-to-b from-rose-50 to-white" data-testid="love-stories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-rose-500 font-medium tracking-wide uppercase text-sm mb-4 block">{t('landing.realCouplesRealLove')}</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-rose-900 mb-4">{t('landing.loveStoriesTitle')}</h2>
            <p className="text-rose-600">{t('landing.loveStoriesSubtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Show dynamic testimonials if available, otherwise show love stories */}
            {(testimonials.length > 0 ? testimonials : loveStories).slice(0, 3).map((story, index) => (
              <div 
                key={story.id}
                className="love-story-card"
                data-testid={`love-story-${index}`}
              >
                {story.photo_url && (
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={story.photo_url}
                      alt={story.couple_names}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-serif text-xl text-rose-900">{story.couple_names}</h3>
                    <span className="text-rose-500 text-sm">{story.wedding_date || story.married_date}</span>
                  </div>
                  <p className="text-rose-600 text-sm mb-3">{story.location}</p>
                  <p className="text-rose-700 text-sm leading-relaxed line-clamp-4">{story.story}</p>
                  {story.is_featured && (
                    <span className="mt-3 inline-block text-amber-600 text-xs font-medium">
                      {t('landing.featuredStory')}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              onClick={() => navigate("/love-stories")}
              variant="outline"
              className="border-2 border-rose-300 text-rose-700 hover:bg-rose-50 rounded-full px-8"
              data-testid="view-all-stories-btn"
            >
              {t('landing.viewAllStories')}
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl text-rose-900 mb-4">{t('landing.howItWorksTitle')}</h2>
            <p className="text-rose-600">{t('landing.howItWorksSubtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: "01", title: t('landing.step1Title'), desc: t('landing.step1Desc') },
              { step: "02", title: t('landing.step2Title'), desc: t('landing.step2Desc') },
              { step: "03", title: t('landing.step3Title'), desc: t('landing.step3Desc') }
            ].map((item, index) => (
              <div key={index} className="relative" data-testid={`step-${index}`}>
                <div className="text-6xl font-serif font-bold bg-gradient-to-r from-rose-200 to-pink-200 bg-clip-text text-transparent mb-4">{item.step}</div>
                <h3 className="font-serif text-xl text-rose-900 mb-2">{item.title}</h3>
                <p className="text-rose-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-rose-500 via-pink-500 to-purple-500 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-10">
          <div className="islamic-pattern w-full h-full" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-6">Ready to Find Your Soulmate?</h2>
          <p className="text-rose-100 mb-8 max-w-xl mx-auto">
            Join thousands of Muslims who have found their life partners through SingleSeen. 
            Your halal journey to marriage starts here.
          </p>
          <Button 
            onClick={() => navigate("/register")}
            className="bg-white text-rose-600 hover:bg-rose-50 rounded-full px-8 py-6 text-lg font-medium shadow-lg"
            data-testid="cta-register-btn"
          >
            Create Free Account
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-rose-900 text-rose-200" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Footer Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div className="col-span-1">
              <img src={LOGO_URL} alt="SingleSeen" className="h-32 w-auto mb-4 brightness-0 invert" />
              <p className="text-rose-300 text-sm">Get seen, find your partner in Deen</p>
            </div>
            
            {/* Quick Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">Quick Links</h4>
              <div className="flex flex-col gap-2 text-sm">
                <Link to="/love-stories" className="hover:text-white transition-colors">Love Stories</Link>
                <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
                <Link to="/browse" className="hover:text-white transition-colors">Browse Profiles</Link>
                <Link to="/register" className="hover:text-white transition-colors">Get Started</Link>
              </div>
            </div>
            
            {/* Support */}
            <div>
              <h4 className="font-semibold text-white mb-4">Support</h4>
              <div className="flex flex-col gap-2 text-sm">
                <Link to="/help" className="hover:text-white transition-colors">Help Center</Link>
                <Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link>
                <Link to="/report" className="hover:text-white transition-colors">Report an Issue</Link>
                <a href="mailto:Matchmaker@singleseen.com" className="hover:text-white transition-colors">Matchmaker@singleseen.com</a>
              </div>
            </div>
            
            {/* Legal */}
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <div className="flex flex-col gap-2 text-sm">
                <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
                <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              </div>
            </div>
          </div>
          
          {/* Bottom Bar */}
          <div className="border-t border-rose-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm">© 2024 SingleSeen. All rights reserved.</p>
            <p className="text-sm text-rose-400">Built with love for the Muslim community</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
